/**
 * El alta: leer en qué punto está y activar el canal gestionado.
 *
 * ── POR QUÉ EL ALTA SE DISPARA AL GUARDAR LA HOJA DE VIDA ──────────────────
 *
 * Con el camino de Gmail escondido, la primera pantalla del alta ya no es una
 * elección: es una bienvenida. Cobrarle a esa bienvenida un POST que crea la
 * membresía deja cuentas a medias de todo el que abrió la página y se fue.
 *
 * Así que la membresía nace en el momento en que hay algo que guardar en ella
 * —el archivo que subió o el cuestionario que contestó—, y ambos caminos pasan
 * por acá antes de escribir. Es idempotente del lado del servidor: llamarlo dos
 * veces devuelve el mismo slug y la misma dirección.
 */
import { apiFetch } from '~/lib/api';
import { messages } from '~/i18n';
import type { OnboardingStatus } from '../lib/onboarding';

export class OnboardingError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, options: { code: string; status: number }) {
    super(message);
    this.name = 'OnboardingError';
    this.code = options.code;
    this.status = options.status;
  }
}

type ClientCode = keyof ReturnType<typeof messages>['onboarding']['client'];

/** Los textos se leen al lanzar, no al cargar el módulo: el idioma es el de ese momento. */
function texts() {
  return messages().onboarding.client;
}

function isClientCode(code: string): code is ClientCode {
  return Object.hasOwn(texts(), code);
}

export async function fetchOnboardingStatus(): Promise<OnboardingStatus> {
  const res = await apiFetch('/job-apply/api/onboarding-status');
  if (!res.ok) {
    throw new OnboardingError(res.status === 401 ? texts().unauthorized : texts().unknown, {
      code: res.status === 401 ? 'unauthorized' : 'unknown',
      status: res.status,
    });
  }
  return (await res.json()) as OnboardingStatus;
}

/**
 * Deja a la persona con un canal por donde salir. Idempotente: si ya tiene
 * membresía en el canal gestionado, el servidor devuelve la misma y no cambia
 * nada. NO toca a quien está en Gmail — ese camino no pasa por acá.
 */
export async function ensureManagedMembership(): Promise<void> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/onboarding/email-channel', { method: 'POST' });
  } catch {
    throw new OnboardingError(texts().network, { code: 'network', status: 0 });
  }

  if (res.ok) return;

  const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  const code = body?.code ?? (res.status === 401 ? 'unauthorized' : 'unknown');
  throw new OnboardingError((isClientCode(code) ? texts()[code] : undefined) ?? body?.error ?? texts().unknown, {
    code,
    status: res.status,
  });
}
